import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import {
  BookOpen,
  FileText,
  Code,
  Rocket,
  HelpCircle,
  ChevronRight,
  Search,
  ExternalLink,
  Download,
  ArrowLeft,
  Crown
} from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import { useTranslation } from '../lib/i18n';

type ExportFormat = 'md' | 'pdf' | 'docx' | 'html' | 'xml' | 'rtf';

const Documentation = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'user'>('all');
  const [downloading, setDownloading] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('md');
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const docs = [
    {
      id: 'user',
      title: t('docs.userGuideTitle'),
      description: t('docs.userGuideDesc'),
      icon: BookOpen,
      category: 'user',
      path: '/docs/USER_GUIDE.md',
      external: true
    }
  ];

  const filteredDocs = docs.filter(doc =>
    (activeTab === 'all' || doc.category === activeTab) &&
    (doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
     doc.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const categories = [
    { id: 'all', label: t('docs.allDocs'), icon: BookOpen },
    { id: 'user', label: t('docs.userGuide'), icon: FileText },
  ];

  const exportFormats: Array<{
    value: ExportFormat;
    label: string;
    extension: string;
    mimeType: string;
    requiresPlan?: 'free' | 'basic' | 'ai' | 'team';
  }> = [
    { value: 'md', label: 'Markdown', extension: 'md', mimeType: 'text/markdown' },
    { value: 'pdf', label: 'PDF', extension: 'pdf', mimeType: 'application/pdf' },
    { value: 'docx', label: 'DOCX', extension: 'docx', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', requiresPlan: 'basic' },
    { value: 'html', label: 'HTML', extension: 'html', mimeType: 'text/html', requiresPlan: 'ai' },
    { value: 'xml', label: 'XML', extension: 'xml', mimeType: 'application/xml', requiresPlan: 'team' },
    { value: 'rtf', label: 'RTF', extension: 'rtf', mimeType: 'application/rtf', requiresPlan: 'team' },
  ];

  const isFormatAvailable = (format: ExportFormat): boolean => {
    const formatConfig = exportFormats.find(f => f.value === format);
    if (!formatConfig || !subscription) return false;

    const availableFormats = subscription.features.exportFormats || [];
    return availableFormats.includes(format);
  };

  const convertToFormat = async (content: string, format: ExportFormat): Promise<Blob> => {
    switch (format) {
      case 'md':
        return new Blob([content], { type: 'text/markdown' });

      case 'pdf':
        return new Blob([content], { type: 'application/pdf' });

      case 'docx':
        const docxContent = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>
    <w:p><w:r><w:t>${content.replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c] || c))}</w:t></w:r></w:p>
  </w:body>
</w:document>`;
        return new Blob([docxContent], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        });

      case 'html':
        const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>DocHelper AI Documentation</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; line-height: 1.6; }
    pre { background: #f4f4f4; padding: 10px; border-radius: 5px; overflow-x: auto; }
    code { background: #f4f4f4; padding: 2px 5px; border-radius: 3px; }
  </style>
</head>
<body>
  <pre>${content}</pre>
</body>
</html>`;
        return new Blob([htmlContent], { type: 'text/html' });

      case 'xml':
        const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<documentation>
  <title>DocHelper AI Documentation</title>
  <content><![CDATA[${content}]]></content>
</documentation>`;
        return new Blob([xmlContent], { type: 'application/xml' });

      case 'rtf':
        const rtfContent = `{\\rtf1\\ansi\\deff0
{\\fonttbl{\\f0 Times New Roman;}}
\\f0\\fs24 ${content.replace(/\n/g, '\\par ')}}`;
        return new Blob([rtfContent], { type: 'application/rtf' });

      default:
        return new Blob([content], { type: 'text/plain' });
    }
  };

  const handleDownloadPackage = async (format: ExportFormat = selectedFormat) => {
    if (!isFormatAvailable(format)) {
      alert(t('docs.requiresHigherAlert', { format: format.toUpperCase() }));
      navigate('/pricing');
      return;
    }

    setDownloading(true);
    try {
      const response = await fetch('/docs/USER_GUIDE.md');
      const content = await response.text();

      const blob = await convertToFormat(content, format);
      const formatConfig = exportFormats.find(f => f.value === format);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `DocHelper-AI-Documentation.${formatConfig?.extension || 'txt'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowFormatMenu(false);
    } catch (error) {
      console.error('Error downloading documentation:', error);
      alert(t('docs.failedDownload'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('docs.title')}</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            {t('docs.subtitle')}
          </p>
        </div>
        <Link to="/">
          <Button variant="outline" className="flex items-center space-x-2">
            <ArrowLeft className="w-4 h-4" />
            <span>{t('docs.backToApp')}</span>
          </Button>
        </Link>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder={t('docs.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {categories.map((category) => (
          <Button
            key={category.id}
            variant={activeTab === category.id ? 'default' : 'outline'}
            onClick={() => setActiveTab(category.id as any)}
            className="flex items-center space-x-2"
          >
            <category.icon className="w-4 h-4" />
            <span>{category.label}</span>
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDocs.map((doc) => (
          <a
            key={doc.id}
            href={doc.path}
            target={doc.external ? "_blank" : "_self"}
            rel={doc.external ? "noopener noreferrer" : ""}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200 flex flex-col h-full"
          >
            <div className="p-6 flex-1">
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <doc.icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                {doc.external && (
                  <ExternalLink className="w-4 h-4 text-gray-400" />
                )}
              </div>
              <h3 className="text-xl font-semibold mb-2">{doc.title}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                {doc.description}
              </p>
            </div>
            <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                {t('docs.viewDocumentation')}
              </span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </div>
          </a>
        ))}
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-8 text-center">
        <h2 className="text-2xl font-bold mb-4">{t('docs.needFullTitle')}</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
          {t('docs.needFullDesc')}
        </p>

        <div className="flex flex-col items-center gap-4">
          <div className="relative inline-block">
            <Button
              variant="outline"
              className="min-w-[200px] justify-between"
              onClick={() => setShowFormatMenu(!showFormatMenu)}
            >
              <span>{exportFormats.find(f => f.value === selectedFormat)?.label || t('docs.selectFormat')}</span>
              <ChevronRight className={`w-4 h-4 transition-transform ${showFormatMenu ? 'rotate-90' : ''}`} />
            </Button>

            {showFormatMenu && (
              <div className="absolute top-full mt-2 left-0 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 overflow-hidden">
                {exportFormats.map((format) => {
                  const available = isFormatAvailable(format.value);
                  return (
                    <button
                      key={format.value}
                      onClick={() => {
                        setSelectedFormat(format.value);
                        setShowFormatMenu(false);
                      }}
                      disabled={!available}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-between ${
                        !available ? 'opacity-50 cursor-not-allowed' : ''
                      } ${selectedFormat === format.value ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                    >
                      <span className="font-medium">{format.label}</span>
                      {!available && (
                        <Crown className="w-4 h-4 text-yellow-500" title={t('docs.requiresPlan', { plan: format.requiresPlan })} />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <Button
            className="font-bold"
            onClick={() => handleDownloadPackage(selectedFormat)}
            disabled={downloading || !isFormatAvailable(selectedFormat)}
          >
            <Download className="w-4 h-4 mr-2" />
            {downloading ? t('docs.downloading') : t('docs.downloadPackage')}
          </Button>

          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t('docs.currentPlan')}: <span className="font-semibold">{subscription?.plan || t('docs.freePlan')}</span>
            {!isFormatAvailable(selectedFormat) && (
              <div className="mt-2 text-orange-600 dark:text-orange-400">
                {t('docs.requiresHigherPlan', { format: selectedFormat.toUpperCase() })}{' '}
                <button
                  onClick={() => navigate('/pricing')}
                  className="underline hover:text-orange-700 dark:hover:text-orange-300"
                >
                  {t('docs.upgradeNow')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;